import { UTCDateMini } from "@date-fns/utc"
import { isValid } from "date-fns"
import { forEach, round, sumBy } from "lodash"

import { DateType } from "~/constants/graphs"

import { formatWeekRangeLabel } from "~/utils/calendarUtils"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatFiscalWeekLabel } from "~/utils/fiscalCalendar"
import { formatNumber } from "~/utils/formatting"
import { getNumberFormatLocale } from "~/stores/number-format-store"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"
import { formatVariantId } from "~/utils/helpers"
import { orderOfMagnitude } from "~/utils/math"
import { extractColor, tooltipSwatch } from "~/utils/graphUtils"
import { HATCH_PERIOD_PX, projectedHatchBackground } from "~/constants/colors"

const TOOLTIP_DIVIDER = `<div style="border-top:1px;border-color:var(--color-border-base);border-style:dotted;width:100%;"></div>`

const ROWS_BLOCK_STYLE = "padding:8px 12px;display:flex;flex-direction: column;gap:4px;width:100%;"

/** Assumed tooltip content width, for judging a segment too thin to hatch. */
const TYPICAL_BAR_WIDTH_PX = 220

const MIN_SEGMENT_WIDTH_PX = 3

type StackBarSegment = { value: number; color: string; hatched: boolean }

type TooltipRow = StackBarSegment & { stack?: string; markup: string }

const rowsBlock = (markup: string) => `<div style="${ROWS_BLOCK_STYLE}">${markup}</div>`

const joinRows = (rows: TooltipRow[]) => rows.map((row) => row.markup).join("")

function tooltipLine(label: string, value: string): string {
  return `<div style="display:flex;align-items:center;justify-content: space-between;gap:12px;">${label}${value}</div>`
}

function tooltipValueCell(value: string | number, currencySuffix = ""): string {
  return `<span style="text-align: right;font-weight:500;color:var(--color-fg-base);">${value} ${currencySuffix}</span>`
}

/**
 * A section's uppercase name, with the combined value of its rows when it has one. Highcharts' AST
 * allowlist passes `class` through, so the typography is classes rather than inline styles.
 */
function tooltipSectionHeader(
  label: string,
  total: string | number | null = null,
  currencySuffix?: string,
): string {
  return tooltipLine(
    `<div class="label-sm text-fg-muted uppercase tracking-wide">${label}</div>`,
    total === null ? "" : tooltipValueCell(total, currencySuffix),
  )
}

/**
 * A stack's composition as a horizontal bar: one segment per member, widths proportional to their
 * share of the total, in stack order, carrying the same fill as the columns they summarise — solid,
 * or the projected hatch. Empty when nothing contributes.
 */
export function tooltipStackBar(segments: StackBarSegment[]): string {
  const contributing = segments.filter((segment) => segment.value > 0)
  const total = sumBy(contributing, "value")
  if (!total) return ""

  const bars = contributing.map(({ value, color, hatched }, index) => {
    const share = value / total
    // Narrower than one stripe period a hatch reads as a gap, so it fills solid. Judged as a
    // share; the bar's px width is the tooltip's, not known here.
    const background =
      hatched && share >= HATCH_PERIOD_PX / TYPICAL_BAR_WIDTH_PX
        ? projectedHatchBackground(color)
        : color
    const seam =
      index < contributing.length - 1
        ? "border-right:1px solid var(--color-background-background);"
        : ""
    return `<div style="width:${round(share * 100, 2)}%;min-width:${MIN_SEGMENT_WIDTH_PX}px;background:${background};${seam}"></div>`
  })

  return `<div style="display:flex;height:8px;width:100%;border-radius:9999px;overflow:hidden;margin:6px 0 8px;">${bars.join("")}</div>`
}

function formatTooltipValue(
  value: number,
  dataAsPercentage: boolean,
  allDigits?: boolean,
  valueFormatter?: (num: number) => string,
): string | number {
  if (valueFormatter) return valueFormatter(value)
  if (dataAsPercentage && value)
    return (value * 100).toLocaleString(getNumberFormatLocale(), { maximumFractionDigits: 2 }) + "%"
  if (!value) return value
  return allDigits
    ? formatNumber(value, Math.max(5 - orderOfMagnitude(value), 0))
    : formatNumber(value, 1)
}

type DateTooltipOptions = {
  dataAsPercentage?: boolean
  showCurrency?: boolean
  currency?: string
  allDigits?: boolean
  stackSectionLabels?: Record<string, string>
  unsectionedLabel?: string
  /**
   * Categories whose column is one figure rather than a composition: the tooltip states the column's
   * total alone, with no series rows, so a series that only exists to colour the bar is not named as if
   * it explained the number.
   */
  plainCategories?: readonly string[]
}

export function dateGraphTooltipFormatter(
  this: Highcharts.Point,
  dateFormat: DateType,
  {
    dataAsPercentage = false,
    showCurrency = false,
    currency,
    allDigits,
    stackSectionLabels,
    unsectionedLabel,
    plainCategories,
  }: DateTooltipOptions = {},
) {
  // Read at hover time, not captured when chart options were memoized.
  const fiscalConfig = getStoredFiscalConfig()
  let dateFormatType = DateFormatType.GraphWeek
  switch (dateFormat) {
    case DateType.Time:
      dateFormatType = DateFormatType.GraphTime
      break
    case DateType.Daily:
      dateFormatType = DateFormatType.GraphDay
      break
    case DateType.Weekly:
      dateFormatType = DateFormatType.GraphWeek
      break
    case DateType.Monthly:
      dateFormatType = DateFormatType.GraphMonth
      break
    case DateType.Quarterly:
      dateFormatType = DateFormatType.GraphQuarter
      break
    case DateType.MonthlyNoYear:
      dateFormatType = DateFormatType.GraphMonthWithoutYear
      break
  }

  const header = isValid(this.category)
    ? fiscalConfig
      ? formatFiscalWeekLabel(new UTCDateMini(this.category), fiscalConfig, dateFormatType)
      : formatDate(new UTCDateMini(this.category), dateFormatType)
    : (this.category as string | number)

  // Weekly headers show the week's Mon→Sun dates so a (possibly fiscal) week number maps to
  // real calendar dates.
  const weekRange =
    dateFormatType === DateFormatType.GraphWeek && isValid(this.category)
      ? formatWeekRangeLabel(new UTCDateMini(this.category))
      : null

  let returnString = `
    <div style="padding:8px 12px;">
      <span style="font-weight:500;">${header}</span>${weekRange ? `<span style="font-weight:400;color:var(--color-fg-muted);"> · ${weekRange}</span>` : ""}
    </div>
  `
  returnString += TOOLTIP_DIVIDER

  const currencySuffix = showCurrency ? (currency ?? "DKK") : ""
  const rows: TooltipRow[] = []

  forEach(this.points, (point) => {
    const pointOptions = ((point as unknown as { point?: { options?: { custom?: unknown } } }).point
      ?.options ?? (point.options as { custom?: unknown } | undefined)) as
      | { custom?: { tooltipValue?: number | null } }
      | undefined
    const tooltipOverride = pointOptions?.custom?.tooltipValue
    const rawValue: number | null | undefined =
      tooltipOverride !== undefined ? tooltipOverride : point.y
    if (rawValue == null) return

    // Columns that are semantically dashed, or filled with a hatch, say so in custom.
    const seriesOptions = point.series.userOptions as {
      dashStyle?: string
      stack?: string
      custom?: {
        swatchDashStyle?: string
        hatched?: boolean
        valueFormatter?: (n: number) => string
      }
    }
    const y = formatTooltipValue(
      rawValue,
      dataAsPercentage,
      allDigits,
      seriesOptions.custom?.valueFormatter,
    )
    const tooltipColor = extractColor(point.color)
    const swatch = tooltipSwatch(
      tooltipColor,
      seriesOptions.custom?.swatchDashStyle ?? seriesOptions.dashStyle,
    )

    rows.push({
      stack: seriesOptions.stack,
      value: rawValue,
      color: tooltipColor,
      hatched: seriesOptions.custom?.hatched ?? false,
      markup: tooltipLine(
        `<div style="display:flex;align-items:center;">${swatch}<div style="margin-left:8px;margin-right:4px;font-weight:400;">${point.series.name}</div></div> `,
        tooltipValueCell(y, currencySuffix),
      ),
    })
  })

  // Sections follow their members' series order, not the label keys' order.
  const sections = Object.entries(stackSectionLabels ?? {})
    .map(([stack, label]) => ({ label, members: rows.filter((row) => row.stack === stack) }))
    .filter(({ members }) => members.length > 0)
    .sort((a, b) => rows.indexOf(a.members[0]!) - rows.indexOf(b.members[0]!))

  const blocks: string[] = []

  if (plainCategories?.includes(String(this.category)) && rows.length > 0) {
    // The column is one figure: its total, and nothing to break it into.
    blocks.push(
      rowsBlock(
        tooltipLine(
          "",
          tooltipValueCell(
            formatTooltipValue(sumBy(rows, "value"), dataAsPercentage, allDigits),
            currencySuffix,
          ),
        ),
      ),
    )
  } else if (!sections.length) {
    blocks.push(rowsBlock(joinRows(rows)))
  } else {
    // Naming the leading rows only helps when a section stands beside them.
    const leadHeader = unsectionedLabel ? tooltipSectionHeader(unsectionedLabel) : ""
    const leadRows = rows.filter((row) => !row.stack || !stackSectionLabels?.[row.stack])
    if (leadRows.length) blocks.push(rowsBlock(leadHeader + joinRows(leadRows)))

    for (const { label, members } of sections) {
      // One member's value is the row below it and its share is the whole bar, so it needs no summary.
      const composed = members.length > 1
      blocks.push(
        rowsBlock(
          tooltipSectionHeader(
            label,
            composed
              ? formatTooltipValue(sumBy(members, "value"), dataAsPercentage, allDigits)
              : null,
            currencySuffix,
          ) +
            (composed ? tooltipStackBar(members) : "") +
            joinRows(members),
        ),
      )
    }
  }

  returnString += blocks.join(TOOLTIP_DIVIDER)

  return `<div style="z-index:9999;box-shadow:
0px 0px 0px 1px rgba(0, 0, 0, 0.08),
0px 4px 8px 0px rgba(0, 0, 0, 0.08),
0px 8px 16px 0px rgba(0, 0, 0, 0.08); border-radius:12px;border:1px solid var(--color-border-base);background:var(--color-background-background);overflow:hidden;" > ${returnString}</div>`
}

export function sizeSplitGraphTooltipFormatter(
  this: Highcharts.Point,
  showPercentageOfTotal: boolean,
  seriesTotals: Record<string, number>,
) {
  let returnString = `
    <div style="padding:8px 12px;">
      <span style="font-weight:500;">${this.category}</span>
    </div>`

  returnString += TOOLTIP_DIVIDER

  returnString += `<div style="${ROWS_BLOCK_STYLE}">`

  forEach(this.points, (point) => {
    const seriesName = point.series.name
    const seriesTotal = seriesTotals[seriesName] || 0

    const value = point.y ?? 0
    const actualValue = showPercentageOfTotal ? Math.round(value * seriesTotal) : Math.round(value)
    const percentage = showPercentageOfTotal ? value : seriesTotal > 0 ? value / seriesTotal : 0

    const valueToShow = showPercentageOfTotal
      ? `${formatNumber(percentage * 100, 1)}% (${formatNumber(actualValue, 1)} units)`
      : `${formatNumber(actualValue, 1)} units (${formatNumber(percentage * 100, 1)}%)`

    returnString += `
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <div style="display:flex;align-items:center;">
          <div style="border-radius:4px;width:16px;height:6px;background-color:${point.color}"></div>
          <div style="margin-left:8px;margin-right:4px;font-weight:400;">
            ${seriesName}
          </div>
        </div>
        <span style="text-align:right;font-weight:500;color:var(--color-fg-base);">
          ${valueToShow}
        </span>
      </div>`
  })

  returnString += "</div>"

  return `
    <div style="
    z-index:9999;
      box-shadow:
        0px 0px 0px 1px rgba(0, 0, 0, 0.08),
        0px 4px 8px 0px rgba(0, 0, 0, 0.08),
        0px 8px 16px 0px rgba(0, 0, 0, 0.08);
      border-radius:12px;
      border:1px solid var(--color-border-base);
      background:var(--color-background-background);
      overflow:hidden;
    ">
      ${returnString}
    </div>`
}

export function doughnutGraphTooltipFormatter(
  this: Highcharts.Point,
  dataAsPercentage: boolean | undefined,
  subtitleLabel: string | undefined,
) {
  let returnString = `
    <div style="padding:8px 12px;"><span style="font-weight:500;">${this.key}</span></div>`

  returnString += TOOLTIP_DIVIDER

  returnString += `<div style="${ROWS_BLOCK_STYLE}">`

  const dualReadout = (
    this.options as { custom?: { dualReadout?: { count: number; share: number } } } | undefined
  )?.custom?.dualReadout

  if (dualReadout) {
    const countStr = formatNumber(dualReadout.count, 1)
    const shareStr =
      (dualReadout.share * 100).toLocaleString(getNumberFormatLocale(), {
        maximumFractionDigits: 1,
      }) + "%"
    returnString += `
    <div style="display:flex;align-items:center;justify-content: space-between;gap:12px;">
      <div style="display:flex;align-items:center;">
        <div style="border-radius:4px;width:16px;height:6px;background-color:${this.color}"></div>
        <div style="margin-left:8px;margin-right:4px;font-weight:400;">SKUs</div>
      </div>
      <span style="text-align: right;font-weight:500;color:var(--color-fg-base);">${countStr}</span>
    </div>
    <div style="display:flex;align-items:center;justify-content: space-between;gap:12px;">
      <div style="display:flex;align-items:center;">
        <div style="width:16px;"></div>
        <div style="margin-left:8px;margin-right:4px;font-weight:400;">Demand share</div>
      </div>
      <span style="text-align: right;font-weight:500;color:var(--color-fg-base);">${shareStr}</span>
    </div>`
  } else {
    let y: null | undefined | string | number = this.y

    if (dataAsPercentage && this.y) {
      y = (this.y * 100).toLocaleString(getNumberFormatLocale(), { maximumFractionDigits: 2 }) + "%"
    } else if (this.y) {
      y = formatNumber(this.y, 1)
    }

    returnString += `
    <div style="display:flex;align-items:center;justify-content: space-between;gap:12px;">
      <div style="display:flex;align-items:center;">
        <div style="border-radius:4px;width:16px;height:6px;background-color:${this.color}"></div>
        <div style="margin-left:8px;margin-right:4px;font-weight:400;">${subtitleLabel}</div>
      </div>
      <span style="text-align: right;font-weight:500;color:var(--color-fg-base);">${y}</span>
    </div>`
  }

  returnString += "</div>"

  return `<div style="z-index:100;box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.08),
    0px 4px 8px 0px rgba(0, 0, 0, 0.08),
    0px 8px 16px 0px rgba(0, 0, 0, 0.08); border-radius:12px;border:1px solid var(--color-border-base);background:var(--color-background-background);overflow:hidden;">${returnString}</div>`
}

export const sizeSplitHeatmapTooltipFormatter = function (this: Highcharts.Point) {
  // @ts-expect-error - accessing custom point properties
  const size = this.point.size || "Unknown"
  const weight = this.value || 0

  let returnString = `
    <div style="padding:8px 12px;"><span style="font-weight:500;">Size: ${size}</span></div>`

  returnString += TOOLTIP_DIVIDER

  returnString += `<div style="${ROWS_BLOCK_STYLE}">`

  returnString += `
    <div style="display:flex;align-items:center;justify-content: space-between;gap:12px;"><div style="font-weight:400;">Size split</div> <span style="text-align: right;font-weight:500;color:var(--color-fg-base);">${weight.toFixed(1)}%</span></div>`

  returnString += "</div>"

  return `<div style="z-index:9999;box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.08),
    0px 4px 8px 0px rgba(0, 0, 0, 0.08),
    0px 8px 16px 0px rgba(0, 0, 0, 0.08); border-radius:12px;border:1px solid var(--color-border-base);background:var(--color-background-background);overflow:hidden;">${returnString}</div>`
}

interface ScatterPointCustom {
  variantId?: string
  variantName?: string
  productName?: string
  imageUrl?: string
}

export function scatterGraphTooltipFormatter(this: Highcharts.Point) {
  // In scatter charts, 'this' is the Point directly
  const seriesName = this.series?.name ?? ""
  const y = this.y ?? 0
  const options = this.options as { name?: string; custom?: ScatterPointCustom } | undefined
  const pointName = options?.name
  const custom = options?.custom

  const hasVariantInfo = custom?.variantId || custom?.variantName

  let returnString = ""

  if (hasVariantInfo) {
    const variantName = custom?.variantName ?? "Unknown"
    const productName = custom?.productName
    const variantId = custom?.variantId
    const imageUrl = custom?.imageUrl

    const title =
      productName && !variantName.includes(productName)
        ? `${productName} / ${variantName}`
        : variantName

    returnString += `
      <div style="padding:8px 12px;display:flex;align-items:center;gap:8px;overflow:hidden;max-width:280px;">
        ${
          imageUrl
            ? `<img src="${imageUrl}" style="width:32px;height:32px;min-width:32px;border-radius:4px;object-fit:cover;" />`
            : `<div style="width:32px;height:32px;min-width:32px;border-radius:4px;background-color:rgba(255,255,255,0.1);"></div>`
        }
        <div style="min-width:0;flex:1;">
          <div style="font-weight:500;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${title}</div>
          ${variantId ? `<div style="font-size:11px;opacity:0.7;">#${formatVariantId(variantId)}</div>` : ""}
        </div>
      </div>`
  } else {
    const header = pointName ?? seriesName
    returnString += `
      <div style="padding:8px 12px;">
        <span style="font-weight:500;">${header}</span>
      </div>`
  }

  returnString += TOOLTIP_DIVIDER

  returnString += `<div style="${ROWS_BLOCK_STYLE}">`

  returnString += `
    <div style="display:flex;align-items:center;justify-content: space-between;gap:12px;">
      <div style="display:flex;align-items:center;">
        <div style="border-radius:4px;width:16px;height:6px;background-color:${this.color}"></div>
        <div style="margin-left:8px;margin-right:4px;font-weight:400;">Sales</div>
      </div>
      <span style="text-align: right;font-weight:500;color:var(--color-fg-base);">${formatNumber(y, 1)}</span>
    </div>`

  returnString += "</div>"

  return `<div style="z-index:9999;box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.08),
    0px 4px 8px 0px rgba(0, 0, 0, 0.08),
    0px 8px 16px 0px rgba(0, 0, 0, 0.08); border-radius:12px;border:1px solid var(--color-border-base);background:var(--color-background-background);overflow:hidden;">${returnString}</div>`
}
